// Company-field table columns — the domain cells (name tile, Active/Hidden
// status chip, monospace SQL column, data source file + column, truncated
// description, absolute last-updated timestamp, last-updated-by avatar, and the
// row-actions kebab) rendered inside the common DataTable. The kebab's menu
// lives in CompanyFieldRowActions — it needs its own open state.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon } from '@/components/common';
import type { DataTableColumn } from '@/components/common';
import type { CompanyField, CompanyFieldPerson } from '@/api/endpoints/companyFieldsApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import CompanyFieldRowActions from './CompanyFieldRowActions';
import { fmtDateTime } from '@/utils/formatDate';

const PersonCell = ({ person }: { person: CompanyFieldPerson | null }) => {
  if (!person) return <span className='company-field-table__muted'>—</span>;
  return (
    <span className='company-field-table__person'>
      <Avatar size={28} tone={toneOf(person.fullName)} initials={initialsOf(person.fullName)} />
      {person.fullName}
    </span>
  );
};

interface UseCompanyFieldColumnsArgs {
  /** Row kebab — verb-gated by the page: absent = hidden in the kebab (the
   *  kebab itself renders nothing when all three are absent). */
  onEdit?: (field: CompanyField) => void;
  onDuplicate?: (field: CompanyField) => void;
  onToggleStatus?: (field: CompanyField) => void;
}

export const useCompanyFieldColumns = ({
  onEdit,
  onDuplicate,
  onToggleStatus,
}: UseCompanyFieldColumnsArgs): DataTableColumn<CompanyField>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'fieldTitle',
      label: t('data_structure_cf_col_name'),
      width: 'minmax(12rem, 1.4fr)',
      render: (f) => (
        <span className='company-field-table__name'>
          <span className='company-field-table__tile' aria-hidden='true'>
            <Icon name='building-2' size={18} />
          </span>
          <span className='company-field-table__title'>{f.fieldTitle}</span>
        </span>
      ),
    },
    {
      key: 'isHidden',
      label: t('data_structure_cf_col_status'),
      render: (f) =>
        f.isHidden ? (
          <Chip iconStart='eye-off'>{t('data_structure_cf_status_hidden')}</Chip>
        ) : (
          <Chip variant='success' iconStart='circle-check'>
            {t('data_structure_cf_status_active')}
          </Chip>
        ),
    },
    {
      key: 'dbColumn',
      label: t('data_structure_cf_col_sql'),
      // SQL identifiers are always latin/lowercase — keep them LTR in Arabic.
      render: (f) => (
        <code className='company-field-table__sql' dir='ltr'>
          {f.dbColumn}
        </code>
      ),
    },
    {
      key: 'dataSourceName',
      label: t('data_structure_cf_col_source'),
      render: (f) => f.dataSourceName || '—',
    },
    {
      key: 'dataSourceColumnName',
      label: t('data_structure_cf_col_column'),
      render: (f) => f.dataSourceColumnName || '—',
    },
    {
      key: 'description',
      label: t('data_structure_cf_col_description'),
      width: 'minmax(12rem, 1.6fr)',
      render: (f) =>
        f.description ? (
          <span className='company-field-table__desc' title={f.description}>
            {f.description}
          </span>
        ) : (
          <span className='company-field-table__muted'>—</span>
        ),
    },
    {
      key: 'updatedAt',
      label: t('data_structure_cf_col_updated'),
      render: (f) => fmtDateTime(f.updatedAt),
    },
    {
      key: 'lastUpdatedBy',
      label: t('data_structure_cf_col_updated_by'),
      width: 'minmax(11rem, 1fr)',
      render: (f) => <PersonCell person={f.lastUpdatedBy} />,
    },
    {
      key: 'actions',
      label: '',
      width: '3rem',
      align: 'end',
      render: (f) => (
        <CompanyFieldRowActions
          field={f}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onToggleStatus={onToggleStatus}
        />
      ),
    },
  ];
};

export default useCompanyFieldColumns;
